import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Star } from '@sorare/core/src/atoms/icons/Star';
import { iconDoorOpen } from '@sorare/core/src/atoms/icons/system/iconDoorOpen.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { Manager } from '@sorare/core/src/components/seo/Team/Manager';
import { Squad as DumbSquad } from '@sorare/core/src/components/seo/Team/Squad';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { FILTERS } from '@sorare/core/src/lib/filters';
import {
  getAppearancePercentage,
  playableFootballPositions,
  positionNames,
} from '@sorare/core/src/lib/players';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { metadatas } from '@sorare/core/src/lib/seo/football';
import { PlayerGameplayTier } from '@sorare/gameplay/src/components/player/PlayerGameplayTier';

import { countryMessages } from '#src/pages/seo/countries/utils.tsx';

import type {
  NationalTeamPlayersQuery,
  NationalTeamPlayersQueryVariables,
} from './__generated__/page.graphql.ts';

const NATIONAL_TEAM_PLAYERS_QUERY = gql`
  query NationalTeamPlayersQuery($countryCode: String!) {
    football {
      nationalTeam(countryCode: $countryCode) {
        slug
        name
        activeMemberships(first: 100) {
          nodes {
            id
            startDate
            aggregatedStats {
              id
              appearances
            }
            player {
              slug
              sport
              displayName
              avatarPictureUrl: pictureUrl(derivative: "avatar")
              position
              age
              gameplayTier
              country {
                slug
                code
                threeLetterCode
              }
              ...getAppearancePercentage_anyPlayerInterface
              ...PlayerScore_anyPlayer
            }
          }
        }
      }
    }
  }
  ${getAppearancePercentage.fragments.anyPlayer}
  ${PlayerScore.fragments.anyPlayer}
` as TypedDocumentNode<
  NationalTeamPlayersQuery,
  NationalTeamPlayersQueryVariables
>;

const StyledVertical = styled(Vertical)`
  align-items: end;
`;

type Membership =
  NationalTeamPlayersQuery['football']['nationalTeam']['activeMemberships']['nodes'][number];

const Players = () => {
  const { countryCode } = useParams();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { formatMessage } = useIntl();
  const { data, loading } = useQuery(NATIONAL_TEAM_PLAYERS_QUERY, {
    variables: { countryCode: countryCode || '' },
    skip: !countryCode,
  });

  useTitleAndDescription(
    metadatas.clubSquad.title,
    metadatas.clubSquad.description,
    {
      club: data
        ? formatMessage(countryMessages.nationalTeam, {
            country: data.football.nationalTeam.name,
          })
        : undefined,
    }
  );

  const membershipsByPosition = useMemo(() => {
    return (data?.football.nationalTeam.activeMemberships.nodes.reduce(
      (acc, membership) => ({
        ...acc,
        [membership.player.position]: [
          ...(acc[membership.player.position] || []),
          membership,
        ],
      }),
      {} as Record<Position, Membership[]>
    ) || {}) as Record<Position, Membership[]>;
  }, [data?.football.nationalTeam.activeMemberships.nodes]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const displayName = formatMessage(countryMessages.nationalTeam, {
    country: data.football.nationalTeam.name,
  });

  const coach = membershipsByPosition[Position.Coach]?.[0];

  const blocks = {
    squad: (
      <DumbSquad
        displayName={displayName}
        stats={['tier', 'l5', 'l15', 'played']}
        statWidths={{ tier: 7 }}
        headers={{
          tier: <Star size={1} role="img" />,
          l5: FILTERS.lastFiveAverageScore.alias,
          l15: FILTERS.lastFifteenAverageScore.alias,
          played: <FontAwesomeIcon icon={iconDoorOpen} />,
        }}
        groups={playableFootballPositions
          .filter(position => !!membershipsByPosition[position])
          .map(position => ({
            label: formatMessage(positionNames[position]),
            players: membershipsByPosition[position as Position]
              .sort(
                (a, b) =>
                  b.aggregatedStats.appearances - a.aggregatedStats.appearances
              )
              .map(membership => ({
                ...membership.player,
                pictureUrl: membership.player.avatarPictureUrl,
                to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                  params: { slug: membership.player.slug },
                  sport: membership.player.sport,
                }),
                stats: {
                  tier: (
                    <StyledVertical>
                      {!!membership.player.gameplayTier && (
                        <PlayerGameplayTier
                          gameplayTier={membership.player.gameplayTier}
                        />
                      )}
                    </StyledVertical>
                  ),
                  l5: (
                    <StyledVertical gap={0.5}>
                      <PlayerScore
                        player={membership.player}
                        mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                      />
                      <LabelS as="p" color="var(--c-nd-600)">
                        {getAppearancePercentage(
                          AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                          membership.player
                        )}
                        %
                      </LabelS>
                    </StyledVertical>
                  ),
                  l15: (
                    <StyledVertical gap={0.5}>
                      <PlayerScore
                        player={membership.player}
                        mode={
                          AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
                        }
                      />
                      <LabelS as="p" color="var(--c-nd-600)">
                        {getAppearancePercentage(
                          AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
                          membership.player
                        )}
                        %
                      </LabelS>
                    </StyledVertical>
                  ),
                  played: (
                    <BodyM as="p">
                      {membership.aggregatedStats.appearances}
                    </BodyM>
                  ),
                },
              })),
          }))}
      />
    ),
    manager: !!coach && (
      <Manager
        displayName={displayName}
        manager={{
          displayName: coach.player.displayName,
          pictureUrl: coach.player.avatarPictureUrl,
          country: coach.player.country,
          startDate: coach.startDate,
        }}
      />
    ),
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout aside={[blocks.manager]}>{blocks.squad}</MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.manager}
      {blocks.squad}
    </Section>
  );
};

export default Players;
